<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #header>
			<LayoutFlex padding="0 var(--size-20)" gap="0">
				<CancelHeader />
			</LayoutFlex>
		</template>

		<template #content>
			<!-- Figma 7415:17934: the title 24 under the logo row; the list fills the frame, so Continue sits at
			     its bottom — where the DS pins the footer. -->
			<LayoutFlex padding="var(--size-24) var(--size-20)" gap="var(--size-24)">
				<Text font="--heading-h3" color="--text-dark-primary">What would you like to change in your Kova plan?</Text>

				<!-- :commit-delay-ms="0": the selection gates the Continue CTA, so the commit must be instant. -->
				<Select type="single" :value="selected" gap="var(--size-8)" :commit-delay-ms="0" @ds:change="onChange">
					<template #options>
						<SelectItem v-for="o in CHOICES" :key="o.value" :value="o.value">
							<LayoutFlex padding="0" gap="var(--size-12)" align-items="flex-start">
								<Text font="--text-16-20-semi" color="--text-dark-primary">{{ o.label }}</Text>
								<Text v-if="o.description" font="--text-14-20-med" :color="KOVA.textSecondary">
									{{ o.description }}
								</Text>
							</LayoutFlex>
							<SelectIndicator mode="always" />
						</SelectItem>
					</template>
				</Select>
			</LayoutFlex>
		</template>

		<template #footer>
			<LayoutFlex padding="0 var(--size-20) var(--size-24)" gap="0">
				<Button full-width :disabled="!geo.isOneClickCancelRequired && !selected" @ds:click="onContinue">
					<Text font="--text-16-18-semi">Continue</Text>
				</Button>
			</LayoutFlex>
		</template>
	</LayoutScreen>
</template>

<script setup lang="ts">
// /cancel/commitment/change — Figma `kova cancel flow3` 6562:5160 (now 7415:17934). After the survey, an approved
// commitment chooses between fixing the card and actually cancelling; "cancel" goes on to the exit
// options, or to the classic confirm in the last month of the term (nothing left to buy out).
// Analytics: WHAT_TO_CHANGE (AL-15098); the card change it opens reports `cancellation_what_to_change_screen`.
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import {
	Button,
	LayoutFlex,
	LayoutScreen,
	Select,
	SelectIndicator,
	SelectItem,
	Text,
} from '@simple-life-app/growth-ui-kova'
import CancelHeader from '@/components/CancelHeader.vue'
import { KOVA } from '@/designTokens'
import { afterChange, type ChangeChoice } from '@/cancelFlow/commitment'
import { useCancelFlowStore } from '@/store/cancelFlow'
import { useGeoStore } from '@/store/geo'
import { useSurveyStore } from '@/store/survey'
import { STEP_TITLE, useCancelStepLogging } from '@/analytics'

const router = useRouter()
const flow = useCancelFlowStore()
const survey = useSurveyStore()
const geo = useGeoStore()

const CHOICES: ReadonlyArray<{ value: ChangeChoice; label: string; description?: string }> = [
	{ value: 'update_payment_method', label: 'Update my payment method' },
	{
		value: 'cancel_plan_now',
		label: 'Cancel my Kova plan now',
		description: 'Your treatment and all upcoming orders will stop',
	},
]

const selected = computed<ChangeChoice | ''>(() => {
	const raw = survey.answers['commitment_change']
	return raw === 'update_payment_method' || raw === 'cancel_plan_now' ? raw : ''
})

// The answers' analytics ids (CR 74): "Cancel my Kova plan now" is `cancel_my_plan_now`.
const RESPONSE: Record<ChangeChoice, string> = {
	update_payment_method: 'update_payment_method',
	cancel_plan_now: 'cancel_my_plan_now',
}

const step = useCancelStepLogging(STEP_TITLE.WHAT_TO_CHANGE, {
	getResponses: () => (selected.value ? [RESPONSE[selected.value]] : []),
})

function onChange(next: string | string[]) {
	survey.setAnswer('commitment_change', Array.isArray(next) ? (next[0] ?? '') : next)
	step.record(selected.value ? [RESPONSE[selected.value]] : [])
}

function onContinue() {
	// The route guard keeps non-commitments off this screen, so `stage` is set; the fallback only
	// satisfies the type. California one-click: an empty selection means "cancel".
	const to = afterChange(selected.value || 'cancel_plan_now', flow.stage ?? 'mid_term')
	if (to === '/payment/update') {
		router.push({ path: to, state: { entry: 'cancellation_what_to_change_screen' } })
		return
	}
	router.push(to)
}
</script>
